
import { computed, defineComponent, reactive, watch } from 'vue';
import UErrorContainer from "@/components/common/UErrorContainer.vue";
import UErrorItem from "@/components/common/UErrorItem.vue";
import { required } from '@vuelidate/validators';
import { strongPassword } from '../../validation/Validators';
import useVuelidate from '@vuelidate/core';
import { useI18n } from 'vue-i18n';
import zxcvbn, { ZXCVBNResult } from 'zxcvbn';
import { DEFAULT_MIN_PASSWORD_SCORE } from '../../config/passwordPolicy';

export default defineComponent({
    components: { UErrorContainer, UErrorItem },
    props: {
        modelValue: String,
        isValid: Boolean,
        label: String,
        disabled: Boolean,
        minPasswordLength: Number,
        minPasswordScore: {
            type: Number,
            default: DEFAULT_MIN_PASSWORD_SCORE
        }
    },
    emits: ['update:modelValue', 'update:isValid'],

    setup(props, { emit }) {
        const { t } = useI18n();
        const form = reactive({
            val: props.modelValue ?? "",
        })

        const normalizedMinPasswordScore = computed(() => {
            const score = props.minPasswordScore ?? DEFAULT_MIN_PASSWORD_SCORE;

            return Math.min(4, Math.max(0, Math.round(score)));
        });

        const strengthTranslationKey = (score: number) => {
            if (score <= 1) {
                return 'strongPassword.messages.strengthWeak';
            }

            if (score === 2) {
                return 'strongPassword.messages.strengthFair';
            }

            if (score === 3) {
                return 'strongPassword.messages.strengthGood';
            }

            return 'strongPassword.messages.strengthStrong';
        };
        
        const strongPasswordMessage = computed(() => {
            return t("strongPassword.messages.strongPassword1", {
                length: props.minPasswordLength,
            })
        });

        const passwordRulesMessage = computed(() => {
            return t("changePassword.validations.passwordRules", {
                strength: t(strengthTranslationKey(normalizedMinPasswordScore.value))
            });
        });

        const passwordStrength = computed<ZXCVBNResult | null>(() => {
            if (!form.val) {
                return null;
            }

            return zxcvbn(form.val);
        });

        const showStrengthMeter = computed(() => Boolean(form.val));

        const strengthClass = computed(() => {
            const score = passwordStrength.value?.score ?? 0;

            if (score <= 1) {
                return 'strength-meter-fill--weak';
            }

            if (score === 2) {
                return 'strength-meter-fill--fair';
            }

            if (score === 3) {
                return 'strength-meter-fill--good';
            }

            return 'strength-meter-fill--strong';
        });

        const strengthLabel = computed(() => {
            const score = passwordStrength.value?.score ?? 0;

            return t(strengthTranslationKey(score));
        });

        const strengthWidth = computed(() => {
            const score = passwordStrength.value?.score;

            if (score == null) {
                return '0%';
            }

            return `${(score + 1) * 20}%`;
        });

        const passwordStrengthValidation = () => {
            if (!form.val) {
                return false;
            }

            return (passwordStrength.value?.score ?? 0) >= normalizedMinPasswordScore.value;
        };
            
        const strongPasswordValidation = () => {
            return strongPassword(form.val, props.minPasswordLength) && passwordStrengthValidation();
        };
        const rules = {
            val: { required, strongPasswordValidation }
        };

        const v$ = useVuelidate(rules, form);
        const passwordIsValid = computed(() => Boolean(form.val) && strongPasswordValidation());

        const onChanged = (event: string | undefined) => {
            form.val = event ?? "";
            v$.value.$touch();
            emit('update:modelValue', form.val);
        }

        watch(() => props.modelValue, (value) => {
            form.val = value ?? "";
            if (!form.val) {
                v$.value.$reset();
            }
        });

        watch(passwordIsValid, (value) => {
            emit('update:isValid', value);
        }, { immediate: true });

        return {
            form,
            v$,
            onChanged,
            t,
            passwordRulesMessage,
            strongPasswordMessage,
            showStrengthMeter,
            strengthClass,
            strengthLabel,
            strengthWidth
        }
    },
})
